import React from "react";
import { FiMessageCircle, FiClock, FiUserCheck, FiShield } from "react-icons/fi";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const fadeLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const fadeRight = {
  hidden: { opacity: 0, x: 60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.15 },
  },
};

const titleStagger = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.2 },
  },
};

const ProcessDifference = () => {
  return (
    <section className="bg-[#f8f9fa] py-8 px-6">
      <div className="max-w-6xl mx-auto">

        {/* ===== TOP CENTER TITLE — own whileInView ===== */}
        <motion.div
          className="text-center max-w-2xl mx-auto mb-8"
          variants={titleStagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0 }}
        >
          <motion.p
            variants={fadeUp}
            className="text-[#6c757d] uppercase tracking-wider text-xs md:text-sm font-semibold"
          >
            Our Difference
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="mt-2 text-2xl md:text-3xl font-bold text-[#212529]"
          >
            What Makes Our Process Different
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="mt-2 text-sm text-[#495057] leading-relaxed"
          >
            Our process is built around clarity, accountability, and craftsmanship —
            giving clients confidence from the first conversation to final handoff.
          </motion.p>
        </motion.div>

        {/* ===== MAIN CONTENT — ONE whileInView wrapping all 3 columns ===== */}
        <motion.div
          className="grid lg:grid-cols-3 items-center gap-6"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0 }}
        >

          {/* ===== LEFT SIDE ===== */}
          <motion.div
            className="space-y-4 text-center lg:text-right lg:-mr-4"
            variants={staggerContainer}
          >
            {[
              { number: "15+", label: "Years of Hands-On Experience" },
              { number: "100%", label: "Transparent Project Communication" },
              { number: "Trusted", label: "Proven Process Across Generations" },
            ].map((stat, idx) => (
              <motion.div key={idx} variants={fadeLeft}>
                <h3 className="text-3xl font-bold text-[#212529]">{stat.number}</h3>
                <p className="text-sm text-[#6c757d] mt-1">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* ===== CENTER IMAGE ===== */}
          <motion.div
            className="flex justify-center relative z-10"
            variants={fadeUp}
          >
            <div className="rounded-2xl overflow-hidden shadow-lg w-[300px] h-[300px]">
              <img
                src="/Images/worker-construction.png"
                alt="Our Process"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>

          {/* ===== RIGHT SIDE ===== */}
          <motion.div
            className="space-y-4 lg:-ml-4"
            variants={staggerContainer}
          >
            <motion.p
              variants={fadeRight}
              className="text-sm text-[#495057] leading-relaxed text-center lg:text-left"
            >
              We don't believe construction should feel overwhelming or uncertain.
              From day one, we establish clear expectations, realistic timelines,
              and consistent communication — so clients always know where their
              project stands and what comes next.
            </motion.p>

            {[
              { icon: <FiMessageCircle />, text: "Clear communication at every stage" },
              { icon: <FiClock />, text: "No surprise costs or timeline shifts" },
              { icon: <FiUserCheck />, text: "Dedicated project oversight from start to finish" },
              { icon: <FiShield />, text: "Quality checks before every handoff" },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                variants={fadeRight}
                className="flex items-start gap-3"
              >
                <div className="w-8 h-8 rounded-full bg-[#212529] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white w-4 h-4">{item.icon}</span>
                </div>
                <p className="text-sm text-[#495057]">{item.text}</p>
              </motion.div>
            ))}
          </motion.div>

        </motion.div>
      </div>
    </section>
  );
};

export default ProcessDifference;